/**
 * KYC birthdate display: day/month/year (12/10/1992).
 * Stored and submitted value stays YYYY-MM-DD. Display never uses the browser locale.
 */

export const BIRTHDATE_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

export const BIRTHDATE_WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

function isRealDate(year, month, day) {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false
  if (year < 1900 || year > 2100) return false
  if (month < 1 || month > 12) return false
  if (day < 1 || day > 31) return false
  const dt = new Date(Date.UTC(year, month - 1, day))
  return dt.getUTCFullYear() === year && dt.getUTCMonth() === month - 1 && dt.getUTCDate() === day
}

/** "1990-10-02" or "1990-10-02T00:00:00.000Z" -> "02/10/1990". Invalid -> "". */
export function isoDateToDayMonthYear(value) {
  if (typeof value !== 'string') return ''
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim())
  if (!match) return ''
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (!isRealDate(year, month, day)) return ''
  return `${match[3]}/${match[2]}/${match[1]}`
}

function isoFromDayMonthYear(day, month, year) {
  if (!isRealDate(year, month, day)) return null
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/**
 * Phone keypads type dots or digits, not slashes.
 * "12/10/1992", "04.05.1967", "04/05.1967", "04051967", or "1992-10-12"
 * -> "1992-10-12" / "1967-05-04". Anything else returns null.
 */
export function dayMonthYearToIsoDate(value) {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null

  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed)
  if (iso) {
    return isoFromDayMonthYear(Number(iso[3]), Number(iso[2]), Number(iso[1]))
  }

  const slashed = trimmed.replace(/[.\-\s]+/g, '/')
  const dmy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(slashed)
  if (dmy) {
    return isoFromDayMonthYear(Number(dmy[1]), Number(dmy[2]), Number(dmy[3]))
  }

  const digits = trimmed.replace(/\D/g, '').slice(0, 8)
  if (/^\d{8}$/.test(digits)) {
    return isoFromDayMonthYear(
      Number(digits.slice(0, 2)),
      Number(digits.slice(2, 4)),
      Number(digits.slice(4, 8))
    )
  }
  return null
}

/** Keep the first 8 digits and show them as DD/MM/YYYY while typing. */
export function formatTypedBirthdate(value) {
  if (typeof value !== 'string') return ''
  const digits = value.replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

export function daysInMonth(year, month) {
  if (month < 1 || month > 12) return 0
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

/** YYYY-MM-DD from parts, or null when that calendar day does not exist. */
export function isoFromParts(year, month, day) {
  if (!isRealDate(year, month, day)) return null
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/** Whole years between a calendar birthdate and today. Future dates are negative. */
export function birthdateAgeYears(value, today = new Date()) {
  const parts = splitIsoDate(value)
  if (!parts) return null
  let age = today.getFullYear() - parts.year
  const month = today.getMonth() + 1
  const day = today.getDate()
  if (month < parts.month || (month === parts.month && day < parts.day)) age -= 1
  return age
}

export function splitIsoDate(value) {
  const iso = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())
    ? value.trim()
    : dayMonthYearToIsoDate(value)
  if (!iso) return null
  const [year, month, day] = iso.split('-').map(Number)
  return { year, month, day, iso }
}

/** Keep the chosen day when the month is shorter. 31 February becomes 28 or 29. */
export function clampBirthdateDay(year, month, day) {
  const max = daysInMonth(year, month)
  if (!max) return null
  const next = Math.min(Math.max(Number(day) || 1, 1), max)
  return isoFromParts(year, month, next)
}

/**
 * Monday-first month grid, always 6 weeks.
 * Days outside the month stay visible so the calendar does not jump.
 */
export function buildBirthdateMonthGrid(year, month) {
  if (!isRealDate(year, month, 1)) return []
  const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay()
  const leading = (firstWeekday + 6) % 7
  const count = daysInMonth(year, month)
  const cells = []

  const push = (cellYear, cellMonth, day, inMonth) => {
    cells.push({
      day,
      inMonth,
      iso: isoFromParts(cellYear, cellMonth, day)
    })
  }

  const prevMonth = month === 1 ? 12 : month - 1
  const prevYear = month === 1 ? year - 1 : year
  const prevCount = daysInMonth(prevYear, prevMonth)
  for (let i = 0; i < leading; i++) {
    push(prevYear, prevMonth, prevCount - leading + 1 + i, false)
  }
  for (let day = 1; day <= count; day++) push(year, month, day, true)

  const nextMonth = month === 12 ? 1 : month + 1
  const nextYear = month === 12 ? year + 1 : year
  let nextDay = 1
  while (cells.length < 42) {
    push(nextYear, nextMonth, nextDay, false)
    nextDay += 1
  }
  return cells
}
